import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { formattaRoma, giornoDiRoma, linkChiama, linkWhatsApp } from '../../utils/percorso';
import { useAuth } from '../../context/AuthContext';
import Icona from '../../components/cliente/Icone';
import { Iniziale } from '../../components/staff/Persone';
import { aInput, classeStato, erroreDi, giorno, inizialeDi, quanto, MAX_NOME, MAX_TESTO } from './Contatti';
import '../../components/staff/scheda.css';
import '../../components/staff/contatti.css';

// /admin/contatti/:id: la scheda del contatto (backend/contatti.py). In alto chi è, come ci ha conosciuto, chi lo
// segue e a che punto siamo; sotto il riquadro del **prossimo passo con la data**, i dati (tutti modificabili),
// e in fondo la storia: chi ha fatto cosa e quando. Il bottone «Diventa cliente» crea il cliente con i dati già
// presi, dà il codice di collegamento da dettare alla persona e dice se l'invito è partito.

const CAMPI = [
  ['nome', 'Nome'], ['cognome', 'Cognome'], ['telefono', 'Telefono'], ['email', 'Email'],
  ['cerca_comune', 'Dove cerca'], ['cerca_tipo', 'Che tipo di casa'], ['cerca_budget', 'Budget'],
];

// Le date della storia le scrive il server in UTC senza fuso: lette con new Date() sarebbero l'ora sbagliata
// (d'estate due ore indietro), quindi passano da formattaRoma/giornoDiRoma come nel resto del sito.
const quando = (iso) => {
  if (!iso) return '';
  const ora = formattaRoma(iso, { hour: '2-digit', minute: '2-digit' });
  if (!ora) return '';
  const oggi = giornoDiRoma(iso) === giornoDiRoma(new Date());
  return `${oggi ? 'oggi' : formattaRoma(iso, { day: 'numeric', month: 'short', year: 'numeric' })} · ${ora}`;
};

// La storia: una riga per volta, con chi l'ha scritta e quando (le note a mano hanno il pallino acceso)
const Storia = ({ righe }) => (
  <section className="card r" data-testid="storia-contatto">
    <div className="ttl"><div className="i b"><Icona nome="orologio" /></div><h2>Storia</h2></div>
    <div className="feed">
      {!righe.length && <p className="vuoto">Ancora niente: la storia si riempie da sola.</p>}
      {righe.map((r) => (
        <div key={r.id}>
          <i className={`dot ${r.tipo === 'nota' ? 'k' : ''}`} />
          <div style={{ minWidth: 0 }}>
            <b>{r.testo}</b>
            <span>{[quando(r.data), r.autore].filter(Boolean).join(' · ')}</span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// «Diventa cliente»: se mancano nome e cognome li chiede lì per lì (il server risponde 400). Dopo, il codice.
const DiventaCliente = ({ contatto, onFatto }) => {
  const [chiede, setChiede] = useState(null);   // {nome, cognome} quando il server li vuole
  const [attesa, setAttesa] = useState(false);
  const [fatto, setFatto] = useState(null);     // {cliente_id, codice_collegamento, invito_inviato}

  const manda = async (corpo) => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/contatti/${contatto.id}/diventa-cliente`, corpo || {});
      setFatto(r.data);
      setChiede(null);
      onFatto();
    } catch (e) {
      if (e?.response?.status === 400) {
        setChiede({ nome: contatto.nome || '', cognome: contatto.cognome || '' });
        toast.error(erroreDi(e, 'Servono nome e cognome'));
      } else toast.error(erroreDi(e));
    } finally { setAttesa(false); }
  };

  if (fatto) {
    return (
      <section className="card hero r" data-testid="diventato-cliente">
        <div className="ttl"><div className="i g"><Icona nome="fatto" /></div><h2>È diventato cliente</h2></div>
        <p>Dettate questo codice alla persona: lo scrive quando si registra e ritrova tutto quello che avete già inserito.</p>
        <p className="codice" data-testid="codice-collegamento">{fatto.codice_collegamento}</p>
        <p className="vuoto">{fatto.invito_inviato ? "L'invito con il codice è partito." : "L'invito non è partito: il codice va dato a voce."}</p>
        <div className="acts" style={{ justifyContent: 'flex-start', marginTop: '.8rem' }}>
          <Link className="btn sm p" to={`/admin/clienti/${fatto.cliente_id}`}>Apri la scheda del cliente</Link>
        </div>
      </section>
    );
  }
  if (contatto.cliente_id) {
    return (
      <section className="card r" data-testid="gia-cliente">
        <div className="ttl"><div className="i g"><Icona nome="fatto" /></div><h2>È già un cliente</h2></div>
        <Link className="btn sm" to={`/admin/clienti/${contatto.cliente_id}`}>Apri la scheda del cliente</Link>
      </section>
    );
  }
  if (chiede) {
    return (
      <section className="card r" data-testid="chiedi-nome">
        <div className="ttl"><div className="i w"><Icona nome="persone" /></div><h2>Come si chiama?</h2></div>
        <p className="vuoto">Per creare il cliente servono nome e cognome.</p>
        <form className="form-griglia" onSubmit={(e) => { e.preventDefault(); manda(chiede); }}>
          <label>Nome<input value={chiede.nome} maxLength={MAX_NOME} onChange={(e) => setChiede({ ...chiede, nome: e.target.value })} required /></label>
          <label>Cognome<input value={chiede.cognome} maxLength={MAX_NOME} onChange={(e) => setChiede({ ...chiede, cognome: e.target.value })} required /></label>
          <div style={{ alignSelf: 'end', display: 'flex', gap: '.4rem' }}>
            <button type="submit" className="btn sm p" disabled={attesa}>Crea il cliente</button>
            <button type="button" className="btn sm" onClick={() => setChiede(null)}>Annulla</button>
          </div>
        </form>
      </section>
    );
  }
  return (
    <section className="card r" data-testid="azione-cliente">
      <div className="ttl"><div className="i"><Icona nome="casa" /></div><h2>Diventa cliente</h2></div>
      <p className="vuoto">Nasce il cliente con i dati già presi (anche la ricerca), parte l&apos;invito con il codice di collegamento e questo contatto si chiude tenendo la sua storia.</p>
      <button type="button" className="btn sm p" style={{ marginTop: '.7rem' }} onClick={() => manda(null)} disabled={attesa} data-testid="diventa-cliente">
        {attesa ? 'Creo…' : 'Diventa cliente'}
      </button>
    </section>
  );
};

const SchedaContatto = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const admin = userType === 'admin';
  const [dati, setDati] = useState(null);
  const [modulo, setModulo] = useState(null);      // i campi in modifica
  const [passo, setPasso] = useState({ testo: '', data: '' });
  const [motivo, setMotivo] = useState(null);      // quando si mette «perso»
  const [nota, setNota] = useState('');
  const [elimina, setElimina] = useState(false);

  const carica = useCallback(() => axios.get(`${API}/contatti/${id}`)
    .then((r) => {
      setDati(r.data);
      const c = r.data.contatto;
      setModulo({ ...Object.fromEntries(CAMPI.map(([k]) => [k, c[k] || ''])), fonte: c.fonte, fonte_nome: c.fonte_nome || '', cerca_note: c.cerca_note || '', note: c.note || '' });
      setPasso({ testo: c.prossimo_passo || '', data: aInput(c.prossimo_passo_il) });
    })
    .catch(() => setDati(false)), [id]);
  useEffect(() => { carica(); }, [carica]);

  const salva = async (corpo, messaggio) => {
    try {
      await axios.put(`${API}/contatti/${id}`, corpo);
      if (messaggio) toast.success(messaggio);
      await carica();
      return true;
    } catch (e) { toast.error(erroreDi(e)); return false; }
  };

  if (dati === false) return <main className="st wrap"><p className="vuoto">Non riesco a caricare questo contatto.</p></main>;
  if (!dati || !modulo) return <main className="st wrap"><p className="vuoto">Carico il contatto…</p></main>;

  const c = dati.contatto;
  const stati = dati.stati || {};
  const fonti = dati.fonti || {};
  const persone = dati.persone || [];
  const tel = linkChiama(c.telefono);
  const wa = linkWhatsApp(c.telefono);
  // la casella si ferma dove si ferma il server: prima il testo in più spariva senza dirlo (blocco 3)
  const campo = (k) => ({ value: modulo[k], maxLength: quanto(k),
                          onChange: (e) => setModulo({ ...modulo, [k]: e.target.value }) });

  const cambiaStato = (v) => {
    if (v === 'perso' && !c.perso_motivo) { setMotivo(''); return; }
    salva({ stato: v }, `Stato: ${stati[v]}`);
  };

  const togliDalCestino = async () => {
    try {
      await axios.delete(`${API}/contatti/${id}`);
      toast.success('Contatto eliminato');
      navigate('/admin/contatti');
    } catch (e) { toast.error(erroreDi(e, 'Non eliminato')); }
  };

  const salvaNota = async () => {
    if (!nota.trim()) return;
    try {
      await axios.post(`${API}/contatti/${id}/nota`, { testo: nota });
      setNota('');
      await carica();
    } catch (e) { toast.error(erroreDi(e, 'Nota non salvata')); }
  };

  return (
    <main className="st wrap" data-testid="scheda-contatto">
      <Link className="back" to="/admin/contatti"><Icona nome="indietro" />Contatti</Link>

      <div className="ch r">
        <div className="av" aria-hidden="true">{inizialeDi(c)}</div>
        <div style={{ minWidth: 0 }}>
          <h1>{c.nome_intero}</h1>
          <div className="meta">
            <span className="fnt"><i className={`f-${c.fonte || 'altro'}`} />{c.fonte_testo}{c.fonte_nome ? ` · da ${c.fonte_nome}` : ''}</span>
            {tel && <a href={tel}>{c.telefono}</a>}
            {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
            <span className={`st-pill ${classeStato(c.stato)}`}>{c.stato_testo}</span>
            <select value={c.stato} onChange={(e) => cambiaStato(e.target.value)} aria-label="Stato">
              {Object.entries(stati).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {admin && persone.length > 0 && (
              <select value={c.consulente_id || ''} onChange={(e) => salva({ consulente_id: e.target.value }, 'Cambiato chi lo segue')} aria-label="Chi segue">
                <option value="">Nessuno</option>
                {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            )}
            {c.segue && <span className="who"><Iniziale persona={c.segue} />{c.segue.nome}</span>}
            {c.data_primo_contatto && <span>conosciuto il {giorno(c.data_primo_contatto, true)}</span>}
          </div>
          {c.perso_motivo && <p className="vuoto" style={{ marginTop: '.3rem' }}>Perso: {c.perso_motivo}</p>}
        </div>
        <div className="acts">
          {wa && <a className="btn sm wa" href={wa} target="_blank" rel="noopener noreferrer"><Icona nome="whatsapp" />WhatsApp</a>}
          {tel && <a className="btn sm" href={tel}><Icona nome="telefono" />Chiama</a>}
          {admin && (
            <button type="button" className="btn sm rosso-vuoto ico-sola" onClick={() => setElimina(true)}
              aria-label={`Elimina ${c.nome_intero}`} title="Elimina il contatto"><Icona nome="cestino" /></button>
          )}
        </div>
      </div>

      {motivo !== null && (
        <div className="doppione r" data-testid="chiedi-motivo">
          <b>Perché l&apos;avete perso?</b>
          <form className="campo-nota" onSubmit={async (e) => { e.preventDefault(); if (await salva({ stato: 'perso', perso_motivo: motivo }, 'Segnato come perso')) setMotivo(null); }}>
            <input value={motivo} maxLength={MAX_TESTO} onChange={(e) => setMotivo(e.target.value)} placeholder="es. ha comprato con un'altra agenzia" aria-label="Motivo" required />
            <button type="submit" className="btn sm p">Salva</button>
            <button type="button" className="btn sm" onClick={() => setMotivo(null)}>Annulla</button>
          </form>
        </div>
      )}

      {elimina && (
        <div className="conferma-elimina r" role="alertdialog" aria-label="Eliminare il contatto" data-testid="conferma-elimina">
          <p>Eliminare <b>{c.nome_intero}</b>? Se ne va anche la sua storia, e non si può annullare.</p>
          <button type="button" className="btn sm" onClick={() => setElimina(false)}>Annulla</button>
          <button type="button" className="btn sm rosso-vuoto" onClick={togliDalCestino}>Elimina</button>
        </div>
      )}

      <div className="g2">
        <div className="stack">
          <section className={`card r${c.in_ritardo ? ' ritardo' : ''}`} data-testid="prossimo-passo">
            <div className="ttl"><div className={`i ${c.in_ritardo ? 'r' : 'w'}`}><Icona nome="calendario" /></div><h2>Prossimo passo</h2>
              {c.prossimo_passo_il && <span className={c.in_ritardo ? 'nx tardi' : 'muto'}>{c.in_ritardo ? 'era ' : ''}{giorno(c.prossimo_passo_il, true)}</span>}</div>
            <form className="passo" onSubmit={(e) => { e.preventDefault(); salva({ prossimo_passo: passo.testo, prossimo_passo_il: passo.data || null }, 'Prossimo passo salvato'); }}>
              <label>Cosa<input value={passo.testo} maxLength={MAX_TESTO} onChange={(e) => setPasso({ ...passo, testo: e.target.value })} placeholder="es. richiamarlo e fissare un incontro" aria-label="Prossimo passo" /></label>
              <label>Quando<input type="date" value={passo.data} onChange={(e) => setPasso({ ...passo, data: e.target.value })} aria-label="Data del prossimo passo" /></label>
              <button type="submit" className="btn sm p">Salva</button>
            </form>
          </section>

          <section className="card r" data-testid="dati-contatto">
            <div className="ttl"><div className="i"><Icona nome="persone" /></div><h2>Dati</h2></div>
            <form className="form-griglia" onSubmit={(e) => { e.preventDefault(); salva(modulo, 'Dati salvati'); }}>
              {CAMPI.map(([k, nome]) => <label key={k}>{nome}<input {...campo(k)} /></label>)}
              <label>Come ci ha conosciuto
                <select {...campo('fonte')}>{Object.entries(fonti).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              </label>
              {modulo.fonte === 'passaparola' && <label>Chi ce l&apos;ha mandato<input {...campo('fonte_nome')} /></label>}
              <label style={{ gridColumn: '1/-1' }}>Cosa cerca<input {...campo('cerca_note')} placeholder="due righe bastano" /></label>
              <label style={{ gridColumn: '1/-1' }}>Note<input {...campo('note')} placeholder="solo per lo staff" /></label>
              <div style={{ alignSelf: 'end', display: 'flex', gap: '.4rem' }}>
                <button type="submit" className="btn sm p">Salva i dati</button>
              </div>
            </form>
          </section>
        </div>

        <div className="stack">
          <section className="card r" data-testid="nuova-nota">
            <div className="ttl"><div className="i"><Icona nome="documento" /></div><h2>Aggiungi una nota</h2></div>
            <div className="campo-nota">
              <textarea value={nota} maxLength={MAX_TESTO} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Cosa vi siete detti" aria-label="Nuova nota" />
              <button type="button" className="btn sm p" onClick={salvaNota}>Salva</button>
            </div>
          </section>
          <DiventaCliente contatto={c} onFatto={carica} />
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}><Storia righe={dati.storia || []} /></div>
    </main>
  );
};

export default SchedaContatto;
